import {
  orderRowActions,
  RowActions,
  Button,
  EmptyState,
  Icon,
  KeyValue,
  List,
  ListRow,
  Menu,
  MoreMenu,
  PageHeader,
  Section,
  Skeleton,
  Text,
  Timeline,
  useToast,
  type MenuItem,
  type TimelineItem,
} from '@ui';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import {
  photoUrl,
  useColony,
  useDeleteColony,
  useFeedingSummary,
  useTimeline,
  usePhoto,
  type Colony,
  type Feeding,
  type MiteCount,
  type Note,
  type TimelineEntry,
  type Treatment,
} from '../api';
import { ColonyFormDialog } from '../components/ColonyFormDialog';
import { EndTreatmentDialog } from '../components/EndTreatmentDialog';
import { FamilyTree } from '../components/FamilyTree';
import { FeedingDialog } from '../components/FeedingDialog';
import { MiteCountDialog } from '../components/MiteCountDialog';
import { MoveDialog } from '../components/MoveDialog';
import { NoteDialog } from '../components/NoteDialog';
import { usePhotoPicker } from '../components/PhotoPicker';
import { StatusBadge } from '../components/StatusBadge';
import { StatusDialog } from '../components/StatusDialog';
import { TreatmentDialog } from '../components/TreatmentDialog';
import {
  daysSince,
  formatAge,
  formatAmount,
  formatInfestation,
  formatOriginDate,
  monthLabel,
} from '../format';
import { ColonyYieldCard } from '../../harvest/components/YieldCards';
import { TaskFormDialog } from '../../tasks/components/TaskFormDialog';
import { formatKg } from '../../harvest/format';
import styles from './ColonyDetailPage.module.css';
import { colonyProfileUrl } from '../../reports/api';

type Open =
  | {
      kind:
        | 'edit'
        | 'status'
        | 'move'
        | 'delete'
        | 'treatment'
        | 'feeding'
        | 'mite'
        | 'note'
        | 'task';
    }
  | { kind: 'editTreatment'; treatment: Treatment }
  | { kind: 'endTreatment'; treatment: Treatment }
  | { kind: 'editFeeding'; feeding: Feeding }
  | { kind: 'editMite'; mite: MiteCount }
  | { kind: 'editNote'; note: Note }
  | null;

export function ColonyDetailPage() {
  const { colonyId = '' } = useParams();
  const { t } = useTranslation();
  const colony = useColony(colonyId);
  if (colony.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(colony.error)}
        actions={<BackLink to="/voelker" label={t('nav.colonies')} />}
      />
    );
  }
  if (!colony.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={6} />
      </div>
    );
  }
  return <ColonyDetail colony={colony.data} />;
}

function ColonyDetail({ colony }: { colony: Colony }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<Open>(null);
  const year = new Date().getFullYear();
  const timeline = useTimeline(colony.id);
  const summary = useFeedingSummary(colony.id, year, can('feedings.read'));
  const remove = useDeleteColony();
  const removePhoto = usePhoto('colonies');
  const photo = usePhotoPicker('colonies', colony.id);
  const close = () => setOpen(null);
  const entries = timeline.data ?? [];
  const running = entries.flatMap((e) =>
    e.kind === 'treatment' && e.treatment.long_term && !e.treatment.ended_on ? [e.treatment] : [],
  );
  const lastMite = entries.find((e) => e.kind === 'mite_count');
  const active = colony.status === 'active';

  // One primary action: everything that is recorded for the colony.
  const add: MenuItem[] = [
    ...(can('treatments.write')
      ? [
          {
            key: 'treatment',
            label: t('apiary.quick.treatment'),
            onSelect: () => setOpen({ kind: 'treatment' }),
          },
        ]
      : []),
    ...(can('feedings.write')
      ? [
          {
            key: 'feeding',
            label: t('apiary.quick.feeding'),
            onSelect: () => setOpen({ kind: 'feeding' }),
          },
        ]
      : []),
    ...(can('treatments.write')
      ? [
          {
            key: 'mite',
            label: t('apiary.quick.miteCount'),
            onSelect: () => setOpen({ kind: 'mite' }),
          },
        ]
      : []),
    ...(can('colonies.write')
      ? [{ key: 'note', label: t('apiary.note.add'), onSelect: () => setOpen({ kind: 'note' }) }]
      : []),
    ...(can('tasks.write')
      ? [
          {
            key: 'task',
            label: t('tasks.new'),
            separated: true,
            onSelect: () => setOpen({ kind: 'task' }),
          },
        ]
      : []),
  ];

  const more: MenuItem[] = [
    ...(can('colonies.write')
      ? [
          { key: 'edit', label: t('common.edit'), onSelect: () => setOpen({ kind: 'edit' }) },
          {
            key: 'status',
            label: t('apiary.colony.changeStatus'),
            onSelect: () => setOpen({ kind: 'status' }),
          },
        ]
      : []),
    ...(can('locations.write') && active
      ? [{ key: 'move', label: t('apiary.colony.move'), onSelect: () => setOpen({ kind: 'move' }) }]
      : []),
    ...(can('colonies.write')
      ? [
          {
            key: 'photo',
            label: colony.has_photo ? t('apiary.colony.photo') : t('apiary.colony.addPhoto'),
            separated: true,
            onSelect: photo.open,
          },
        ]
      : []),
    ...(can('colonies.write') && colony.has_photo
      ? [
          {
            key: 'removePhoto',
            label: t('apiary.colony.removePhoto'),
            onSelect: () =>
              removePhoto.mutate(
                { id: colony.id, file: null },
                { onSuccess: () => toast.show(t('apiary.photo.removed')) },
              ),
          },
        ]
      : []),
    {
      key: 'profile',
      label: t('reports.pdf.profile'),
      separated: can('colonies.write'),
      onSelect: () => window.open(colonyProfileUrl(colony.id), '_blank', 'noopener'),
    },
    ...(can('colonies.write')
      ? [
          {
            key: 'delete',
            label: t('common.delete'),
            tone: 'danger' as const,
            separated: true,
            onSelect: () => setOpen({ kind: 'delete' }),
          },
        ]
      : []),
  ];

  const items = entries.map((e, i) => timelineItem(e, i, t, can, setOpen));
  const feeding =
    summary.data && summary.data.totals.length > 0
      ? summary.data.totals.map((s) => `${formatAmount(s.amount)} ${s.unit}`).join(' + ')
      : null;

  return (
    <div className={styles.page}>
      {photo.input}
      <PageHeader
        back={<BackLink to="/voelker" label={t('nav.colonies')} />}
        title={colony.name}
        meta={
          <>
            {colony.location.name} · {formatAge(colony.origin_date, colony.origin_precision)}
            {!active && (
              <>
                {' · '}
                <StatusBadge status={colony.status} />
                {colony.status_date && ` ${formatDate(colony.status_date)}`}
              </>
            )}
          </>
        }
        actions={
          <>
            {add.length > 0 && active && (
              <Menu
                align="end"
                items={add}
                trigger={(p) => (
                  <Button {...p} iconStart={<Icon name="plus" size="sm" />}>
                    {t('apiary.colony.addEntry')}
                  </Button>
                )}
              />
            )}
            <MoreMenu label={t('common.moreActions')} items={more} />
          </>
        }
      />

      <div className={styles.columns}>
        <div className={styles.main}>
          <Section title={t('apiary.colony.timeline')} variant="content">
            {timeline.error && (
              <Text tone="danger" role="alert">
                {errorMessage(timeline.error)}
              </Text>
            )}
            {timeline.isPending && <Skeleton lines={5} />}
            {timeline.data && items.length === 0 && (
              <Text tone="muted">{t('apiary.colony.timelineEmpty')}</Text>
            )}
            {items.length > 0 && <Timeline items={items} label={t('apiary.timeline.label')} />}
          </Section>
        </div>

        <div className={styles.side}>
          {colony.has_photo && (
            <img
              className={styles.photo}
              src={photoUrl(`/colonies/${colony.id}/photo`, colony.version)}
              alt={t('apiary.photo.alt', { name: colony.name })}
            />
          )}
          <Section
            title={t('apiary.colony.details')}
            footer={
              colony.changed.at &&
              (colony.changed.by
                ? t('apiary.colony.changed', {
                    date: formatDateTime(colony.changed.at),
                    name: colony.changed.by,
                  })
                : t('apiary.colony.changedNoName', { date: formatDateTime(colony.changed.at) }))
            }
          >
            <KeyValue
              items={[
                {
                  key: 'location',
                  label: t('apiary.colony.location'),
                  value: (
                    <Link to={`/standorte/${colony.location.id}`}>{colony.location.name}</Link>
                  ),
                },
                ...(can('treatments.read')
                  ? [
                      {
                        key: 'treatment',
                        label: t('apiary.colony.runningTreatment'),
                        value: running[0]
                          ? `${running[0].method_name}, ${t('apiary.colonies.running', {
                              count: daysSince(running[0].started_on),
                            })}`
                          : null,
                      },
                      {
                        key: 'mites',
                        label: t('apiary.colony.miteCount'),
                        value:
                          lastMite?.kind === 'mite_count'
                            ? `${formatInfestation(lastMite.mite_count.infestation)}, ${formatDate(
                                lastMite.mite_count.counted_on,
                              )}`
                            : null,
                      },
                    ]
                  : []),
                ...(can('feedings.read')
                  ? [
                      {
                        key: 'feeding',
                        label: t('apiary.colony.feedingYear', { year }),
                        value: feeding,
                      },
                    ]
                  : []),
                {
                  key: 'originDate',
                  label: t('apiary.colony.originDate'),
                  value: formatOriginDate(colony.origin_date, colony.origin_precision),
                },
                {
                  key: 'origin',
                  label: t('apiary.colony.origin'),
                  value: colony.origin ? t(`apiary.origin.${colony.origin}`) : null,
                },
                {
                  key: 'parent',
                  label: t('apiary.colony.parent'),
                  value: colony.parent && (
                    <Link to={`/voelker/${colony.parent.id}`}>{colony.parent.name}</Link>
                  ),
                },
                {
                  key: 'notes',
                  label: t('apiary.colony.notes'),
                  value: colony.notes && <span className={styles.notes}>{colony.notes}</span>,
                },
              ]}
            />
          </Section>
          <FamilyTree colonyId={colony.id} />
          {can('harvest.read') && <ColonyYieldCard colonyId={colony.id} />}
          {colony.placements.length > 1 && (
            <Section title={t('apiary.colony.history')}>
              <List>
                {colony.placements.map((p, i) => (
                  <ListRow
                    key={`${p.location.id}-${p.since}-${i}`}
                    href={`/standorte/${p.location.id}`}
                    title={p.location.name}
                    trailing={`${formatDate(p.since)} – ${
                      p.until ? formatDate(p.until) : t('apiary.colony.current')
                    }`}
                  />
                ))}
              </List>
            </Section>
          )}
        </div>
      </div>

      {open?.kind === 'edit' && <ColonyFormDialog open colony={colony} onClose={close} />}
      {open?.kind === 'task' && (
        <TaskFormDialog
          open
          context={{ colonyIds: [colony.id], label: colony.name }}
          onClose={close}
        />
      )}
      {open?.kind === 'status' && <StatusDialog open colony={colony} onClose={close} />}
      {open?.kind === 'move' && (
        <MoveDialog
          open
          colonyIds={[colony.id]}
          currentLocationId={colony.location.id}
          onClose={close}
        />
      )}
      {open?.kind === 'treatment' && (
        <TreatmentDialog open colonyIds={[colony.id]} onClose={close} />
      )}
      {open?.kind === 'editTreatment' && (
        <TreatmentDialog open treatment={open.treatment} onClose={close} />
      )}
      {open?.kind === 'endTreatment' && (
        <EndTreatmentDialog open treatment={open.treatment} onClose={close} />
      )}
      {open?.kind === 'feeding' && <FeedingDialog open colonyIds={[colony.id]} onClose={close} />}
      {open?.kind === 'editFeeding' && (
        <FeedingDialog open feeding={open.feeding} onClose={close} />
      )}
      {open?.kind === 'mite' && <MiteCountDialog open colonyId={colony.id} onClose={close} />}
      {open?.kind === 'editMite' && <MiteCountDialog open mite={open.mite} onClose={close} />}
      {open?.kind === 'note' && <NoteDialog open colonyId={colony.id} onClose={close} />}
      {open?.kind === 'editNote' && (
        <NoteDialog open colonyId={colony.id} note={open.note} onClose={close} />
      )}
      <ConfirmDialog
        open={open?.kind === 'delete'}
        onClose={() => {
          remove.reset();
          close();
        }}
        title={t('apiary.colony.deleteTitle', { name: colony.name })}
        description={t('apiary.colony.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          remove.mutate(colony.id, {
            onSuccess: () => {
              toast.show(t('apiary.colony.deleted'));
              void navigate('/voelker');
            },
          })
        }
      />
    </div>
  );
}

function timelineItem(
  e: TimelineEntry,
  index: number,
  t: TFunction,
  can: (p: Parameters<ReturnType<typeof useSession>['can']>[0]) => boolean,
  setOpen: (o: Open) => void,
): TimelineItem {
  const base = {
    key: `${e.kind}-${index}`,
    date: formatDate(e.date),
    dateTime: e.date,
    group: monthLabel(e.date),
  };
  switch (e.kind) {
    case 'created':
      return {
        ...base,
        icon: 'plus',
        title: t('apiary.timeline.created'),
        description: t('apiary.timeline.createdAt', { location: e.location }),
      };
    case 'relocated':
      return {
        ...base,
        icon: 'truck',
        title: t('apiary.timeline.relocated'),
        description: t('apiary.timeline.relocation', { from: e.from, to: e.to }),
      };
    case 'status_changed':
      return {
        ...base,
        icon: 'activity',
        tone: e.status === 'active' ? 'neutral' : 'danger',
        title: t('apiary.timeline.statusChanged', { status: t(`apiary.status.${e.status}`) }),
      };
    case 'treatment': {
      const tr = e.treatment;
      const isRunning = tr.long_term && !tr.ended_on;
      const parts = [
        tr.amount != null ? `${formatAmount(tr.amount, 3)} ${tr.unit ?? ''}`.trim() : null,
        tr.application ? t(`apiary.application.${tr.application}`) : null,
        tr.ended_on
          ? t('apiary.timeline.endedOn', { date: formatDate(tr.ended_on) })
          : isRunning
            ? t('apiary.timeline.running')
            : null,
        tr.performed_by ? t('apiary.timeline.by', { name: tr.performed_by }) : null,
      ].filter(Boolean);
      return {
        ...base,
        icon: 'syringe',
        tone: isRunning ? 'warning' : 'neutral',
        title: t('apiary.timeline.treatment', { method: tr.method_name }),
        description: parts.join(' · '),
        actions: can('treatments.write') ? (
          <RowActions
            label={`${t('common.moreActions')}: ${tr.method_name}`}
            items={orderRowActions([
              {
                key: 'edit',
                label: t('common.edit'),
                onSelect: () => setOpen({ kind: 'editTreatment', treatment: tr }),
              },
              isRunning && {
                key: 'end',
                label: t('apiary.treatment.end'),
                onSelect: () => setOpen({ kind: 'endTreatment', treatment: tr }),
              },
            ])}
          />
        ) : undefined,
      };
    }
    case 'feeding': {
      const f = e.feeding;
      return {
        ...base,
        icon: 'droplet',
        title: t('apiary.timeline.feeding', { type: f.feed_type_name }),
        description: [
          `${formatAmount(f.amount)} ${f.unit}`,
          f.purpose ? t(`apiary.purpose.${f.purpose}`) : null,
          f.performed_by ? t('apiary.timeline.by', { name: f.performed_by }) : null,
        ]
          .filter(Boolean)
          .join(' · '),
        actions: can('feedings.write') ? (
          <RowActions
            label={`${t('common.moreActions')}: ${f.feed_type_name}`}
            items={[
              {
                key: 'edit',
                label: t('common.edit'),
                onSelect: () => setOpen({ kind: 'editFeeding', feeding: f }),
              },
            ]}
          />
        ) : undefined,
      };
    }
    case 'mite_count': {
      const m = e.mite_count;
      const title = t('apiary.timeline.miteCount', { value: formatInfestation(m.infestation) });
      return {
        ...base,
        icon: 'bug',
        title,
        description: `${t(`apiary.miteMethod.${m.method}`)} · ${m.mites}`,
        actions: can('treatments.write') ? (
          <RowActions
            label={`${t('common.moreActions')}: ${title}`}
            items={[
              {
                key: 'edit',
                label: t('common.edit'),
                onSelect: () => setOpen({ kind: 'editMite', mite: m }),
              },
            ]}
          />
        ) : undefined,
      };
    }
    case 'yield_share':
      return {
        ...base,
        icon: 'honeycomb',
        title: t('harvest.timeline.share', {
          amount: formatKg(e.share.grams),
          variety: e.share.variety.name,
        }),
        description: (
          <Link to={`/ernte/${e.share.batch_id}`}>
            {t('harvest.timeline.batch', { lot: e.share.lot })}
          </Link>
        ),
      };
    case 'task':
      return {
        ...base,
        icon: 'list-checks',
        tone: e.task.status !== 'done' && e.task.overdue ? 'warning' : 'neutral',
        title: <Link to={`/aufgaben/${e.task.id}`}>{e.task.title}</Link>,
        description:
          e.task.status === 'done'
            ? e.task.done_by
              ? t('tasks.timeline.doneBy', { name: e.task.done_by })
              : t('tasks.timeline.done')
            : t('tasks.timeline.open'),
      };
    case 'note':
      return {
        ...base,
        icon: 'note',
        title: t('apiary.timeline.note'),
        description: e.note.author ? `${e.note.text} — ${e.note.author}` : e.note.text,
        actions: can('colonies.write') ? (
          <RowActions
            label={`${t('common.moreActions')}: ${t('apiary.timeline.note')}`}
            items={[
              {
                key: 'edit',
                label: t('common.edit'),
                onSelect: () => setOpen({ kind: 'editNote', note: e.note }),
              },
            ]}
          />
        ) : undefined,
      };
  }
}
